import { init as initFrontmanClient } from '@tmap-web-lib/remote-api-client/frontman';
import { TmapApp } from '@tmap-web-lib/tmap-app-interface';
import { getSearchParams } from '@tmap-web-lib/utils';
import { useSetAtom } from 'jotai';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import UAParser from 'ua-parser-js';

import { TmapInfoState, akAtom, getAkStore, tmapInfoAtom } from './store/initialState.ts';

const INIT_TIMEOUT_MS = 5000;

const uaResult = new UAParser(window.navigator.userAgent).getResult();

const Initialize = ({ children }: PropsWithChildren) => {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const setAk = useSetAtom(akAtom);
  const setTmapInfo = useSetAtom(tmapInfoAtom);

  const initialize = useCallback(async () => {
    const searchParams = getSearchParams<{
      ak?: string;
      deviceid?: string;
      carrierName?: string;
      euk?: string;
      sessionid?: string;
      service?: string;
    }>();

    const timeoutId = setTimeout(() => {
      setError(new Error('초기화 시간이 초과되었습니다.'));
    }, INIT_TIMEOUT_MS);

    try {
      const osType = TmapApp.env.isIOS ? 'IOS' : 'AND';
      const osVersion = uaResult.os?.version || '';
      const modelNo = uaResult.device?.model || '';
      const appVersion = TmapApp.env.appVersion ?? 'unknown';

      let tmapInfoState: TmapInfoState;
      let accessKeyForStore = '';

      // 인앱웹뷰 쿼리로 진입한 경우
      if (searchParams.ak && searchParams.sessionid && searchParams.deviceid && searchParams.euk) {
        accessKeyForStore = searchParams.ak;
        const reg = /^[a-zA-Z\d]*$/;
        const carrier = reg.test(searchParams.carrierName ?? '')
          ? searchParams.carrierName!
          : 'ETC';

        tmapInfoState = {
          ak: accessKeyForStore,
          appVersion,
          carrierName: carrier ?? 'ETC',
          deviceid: searchParams.deviceid!,
          euk: searchParams.euk!,
          osType,
          osVersion,
          sessionid: searchParams.sessionid!,
        };
      } else {
        const [accessKey, deviceid, carrierName, euk, sessionid] = await Promise.all([
          TmapApp.getAccessKey(),
          TmapApp.getDeviceId(),
          TmapApp.getCarrierName(),
          TmapApp.getEUK(),
          TmapApp.getSessionId(),
        ]);

        const reg = /^[a-zA-Z\d]*$/;
        const carrier = reg.test(carrierName ?? '') ? carrierName : 'ETC';

        accessKeyForStore = accessKey ?? '';

        tmapInfoState = {
          ak: accessKeyForStore,
          appVersion,
          carrierName: carrier ?? 'ETC',
          deviceid: deviceid ?? '',
          euk: euk ?? '',
          osType,
          osVersion,
          sessionid: sessionid ?? '',
        };
      }

      initFrontmanClient({
        hostUrl: import.meta.env.VITE_PUBLIC_FRONTMAN_ENDPOINT,
        headers: {
          Carrier: tmapInfoState.carrierName,
          'Device-Id': tmapInfoState.deviceid,
          'App-Code': 'TMAP',
          'Os-Type': tmapInfoState.osType,
          'App-Version': tmapInfoState.appVersion,
          'Os-Version': tmapInfoState.osVersion,
          'Model-No': modelNo,
          Using: 'WEB_VERTICAL',
          Euk: tmapInfoState.euk,
        },
        timeout: 30000,
        onRequest(config) {
          config.headers.AccessKey = getAkStore().accessKey || searchParams.ak || '';
          return config;
        },
        onResponse(response) {
          const accessKey = response.headers.accesskey;
          if (accessKey) setAk((prev) => ({ ...prev, accessKey }));
          return response;
        },
        onAccessKeyChanged(newAccessKey: string) {
          if (newAccessKey) {
            TmapApp.updateAccessKey({ key: newAccessKey });
            setAk((prev) => ({ ...prev, accessKey: newAccessKey }));
          }
        },
        onAccessKeyExpired(e) {
          setAk((prev) => ({ ...prev, accessKeyExpired: true }));
          throw e;
        },
        onError(e) {
          throw e;
        },
      });

      if (accessKeyForStore) {
        setAk((prev) => ({ ...prev, accessKey: accessKeyForStore }));
      }

      setTmapInfo(tmapInfoState);
      clearTimeout(timeoutId);
      setIsReady(true);
    } catch (err) {
      clearTimeout(timeoutId);
      setError(err instanceof Error ? err : new Error('초기화에 실패했습니다.'));
    }
  }, [setAk, setTmapInfo]);

  useEffect(() => {
    if (isReady) return;
    initialize();
  }, [isReady, initialize]);

  if (error) {
    throw error;
  }

  if (!isReady) return null;
  return children;
};

export default Initialize;
